import { useAuth } from '../../auth-lib';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '../../design-system';
import { useNotifications } from '../../context/NotificationContext';
import NotificationPanel from '../../components/NotificationPanel';
import {
  DashboardIcon,
  ClientsIcon,
  ServicesIcon,
  CredentialsIcon,
  ReportsIcon,
  FinanceIcon,
  WebsiteIcon,
} from '../../org-admin/components/NavIcons';

// Solid shield logomark for the Admin Portal brand — matches OrgAdminLogo's
// filled style (not a thin UI icon) so the two shells read as one product.
function AdminLogo() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 2.5l7.5 2.8v6.2c0 4.6-3.2 8.3-7.5 9.8-4.3-1.5-7.5-5.2-7.5-9.8V5.3L12 2.5Zm-1 12.9l5-5-1.4-1.4-3.6 3.6-1.6-1.6L8 12.4l3 3Z"
        fill="currentColor"
      />
    </svg>
  );
}

// Same shell chrome as the org-admin cockpit (variant with the global top bar,
// SVG nav icons, and an avatar menu that holds Logout) so the super-admin area
// looks like part of the same product rather than a bare fallback layout.
const NAV_ITEMS = [
  { to: '/admin', icon: <DashboardIcon />, label: 'Dashboard' },
  { to: '/admin/organizations', icon: <ClientsIcon />, label: 'Organizations' },
  // Where firms are let in. Sits directly under Organizations because the two
  // answer the same question from different ends: who exists, and who may work.
  { to: '/admin/subscriptions', icon: <FinanceIcon />, label: 'Subscriptions' },
  { to: '/admin/services', icon: <ServicesIcon />, label: 'Services' },
  { to: '/admin/portals', icon: <CredentialsIcon />, label: 'Portals' },
  // The keys other applications read this platform through. Beside Portals
  // because both are credential surfaces, and neither is a firm's own setting.
  { to: '/admin/integrations', icon: <CredentialsIcon />, label: 'Integrations' },
  { to: '/admin/audit-logs', icon: <ReportsIcon />, label: 'Audit Logs' },
  // The public marketing page. Last in the list because it is the one entry
  // that changes what STRANGERS see, not what a firm sees.
  { to: '/admin/landing', icon: <WebsiteIcon />, label: 'Landing Page' },
];

function AdminLayout({ children, title, subtitle, headerRight }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const { counts } = useNotifications();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <AppLayout
      variant="admin"
      brandIcon={<AdminLogo />}
      brandText="Admin Portal"
      items={NAV_ITEMS}
      onLogout={handleLogout}
      title={title}
      subtitle={subtitle}
      headerRight={headerRight}
      user={{ name: user?.name, role: 'Super Admin' }}
      notificationCount={counts.total}
      renderNotifications={({ close }) => <NotificationPanel close={close} />}
      searchPlaceholder="Search Organizations"
      onSearch={(query) => {
        if (query) navigate(`/admin/organizations?q=${encodeURIComponent(query)}`);
      }}
    >
      {children}
    </AppLayout>
  );
}

export default AdminLayout;
